import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet-async" 
import {
    Calendar, Clock, User, ArrowLeft, Share2, Facebook, Twitter,
    Linkedin, Mail, Tag, TrendingUp, Copy, Check, ChevronRight
} from "lucide-react"
import { loadPublicPost, clearCurrentPost } from "../store/slices/publicSlice"
import { fetchPublicPosts } from "../api/publicApi"
import { getCategoryLabel } from "../utils/categoryLabels"

function estimateReadTime(html) {
    const text = html?.replace(/<[^>]+>/g, '') || ''
    const words = text.split(/\s+/).filter(Boolean).length
    return `${Math.max(1, Math.ceil(words / 200))} min`
}

export default function ArticleDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { currentPost: post, loading } = useSelector((s) => s.public)
    const [relatedPosts, setRelatedPosts] = useState([])
    const [copied, setCopied] = useState(false)
    const [copyError, setCopyError] = useState(false)
    const [shareUrl, setShareUrl] = useState('')

    useEffect(() => {
        dispatch(loadPublicPost(id))
        return () => { dispatch(clearCurrentPost()) }
    }, [dispatch, id])

    useEffect(() => {
        if (typeof window !== 'undefined') setShareUrl(window.location.href)
    }, [post])

    useEffect(() => {
        if (!post?.category) return
        fetchPublicPosts({ category: post.category, limit: 4 }).then((data) => {
            const list = data.posts || []
            setRelatedPosts(list.filter((p) => p._id !== post._id).slice(0, 3))
        }).catch(() => {})
    }, [post?._id, post?.category])

    const shareTitle = post?.title || ''
    const headlineUrl = post?.headlineImage?.url || post?.headlineImage || ''

    const shareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`,
        email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`,
    }

    const handleCopy = () => {
        setCopyError(false)
        const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '')
        if (!url) return
        const doCopy = (text) => {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                return navigator.clipboard.writeText(text)
            }
            return new Promise((resolve, reject) => {
                const input = document.createElement('input')
                input.value = text
                input.setAttribute('readonly', '')
                input.style.position = 'absolute'
                input.style.left = '-9999px'
                document.body.appendChild(input)
                input.select()
                try {
                    const ok = document.execCommand('copy')
                    document.body.removeChild(input)
                    ok ? resolve() : reject(new Error('execCommand failed'))
                } catch (e) {
                    document.body.removeChild(input)
                    reject(e)
                }
            })
        }
        doCopy(url).then(() => {
            setCopied(true)
            setCopyError(false)
            setTimeout(() => setCopied(false), 2000)
        }).catch(() => {
            setCopyError(true)
            setTimeout(() => setCopyError(false), 2000)
        })
    }

    if (loading && !post) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse space-y-6 max-w-4xl w-full px-6">
                    <div className="h-8 bg-secondary rounded w-3/4" />
                    <div className="h-4 bg-secondary rounded w-1/2" />
                    <div className="h-64 bg-secondary rounded-2xl" />
                    <div className="space-y-3">
                        <div className="h-4 bg-secondary rounded" />
                        <div className="h-4 bg-secondary rounded w-5/6" />
                        <div className="h-4 bg-secondary rounded w-4/6" />
                    </div>
                </div>
            </div>
        )
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-foreground mb-4">Article non trouve</h1>
                    <button onClick={() => navigate(-1)} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-all liquid-glass">
                        Retour
                    </button>
                </div>
            </div>
        )
    }

    const authorName = post.author?.fullname || 'Auteur inconnu'
    const authorAvatar = post.author?.profilePicture
    const authorId = post.author?._id
    const readTime = estimateReadTime(post.body)
    const isVideo = post.headlineImage?.resourceType === 'video'

    return (
        <div className="bg-background min-h-screen">
            <Helmet>
                <title>{post.title} - Universite de Ngozi</title>
                <meta name="description" content={post.excerpt || post.title} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.excerpt || ''} />
                <meta property="og:image" content={headlineUrl} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:type" content="article" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.excerpt || ''} />
                <meta name="twitter:image" content={headlineUrl} />
            </Helmet>

            {/* Hero Section */}
            <section className="relative py-8 sm:py-12 bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden">
                <div className="absolute top-10 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-10 left-10 w-24 h-24 bg-accent/5 rounded-full blur-2xl" />

                <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
                    <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors mb-4 sm:mb-6">
                        <ArrowLeft className="w-4 h-4" />
                        Retour
                    </button>

                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-3 sm:mb-4">
                        <Tag className="w-3.5 h-3.5" />
                        <span>{getCategoryLabel(post.category)}</span>
                    </div>

                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4 leading-tight">{post.title}</h1>

                    {post.excerpt && (
                        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4 sm:mb-6">{post.excerpt}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6">
                        <Link to={`/auteur/${authorId}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                            {authorAvatar ? (
                                <img src={authorAvatar} alt={authorName} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                            ) : (
                                <User className="w-4 h-4" />
                            )}
                            <span className="font-medium text-foreground">{authorName}</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span>{new Date(post.publishedAt || post.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span>{readTime} de lecture</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-8 sm:py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
                        {/* Sidebar - Social Share (Desktop) */}
                        <div className="hidden lg:block lg:col-span-1">
                            <div className="sticky top-24 space-y-3">
                                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-card border border-border rounded-lg flex items-center justify-center hover:bg-blue-50 hover:border-blue-200 transition-all group liquid-glass">
                                    <Facebook className="w-4 h-4 text-muted-foreground group-hover:text-blue-600" />
                                </a>
                                <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-card border border-border rounded-lg flex items-center justify-center hover:bg-sky-50 hover:border-sky-200 transition-all group liquid-glass">
                                    <Twitter className="w-4 h-4 text-muted-foreground group-hover:text-sky-500" />
                                </a>
                                <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-card border border-border rounded-lg flex items-center justify-center hover:bg-blue-50 hover:border-blue-200 transition-all group liquid-glass">
                                    <Linkedin className="w-4 h-4 text-muted-foreground group-hover:text-blue-700" />
                                </a>
                                <a href={shareLinks.email} className="w-10 h-10 bg-card border border-border rounded-lg flex items-center justify-center hover:bg-primary/10 hover:border-primary/30 transition-all group liquid-glass">
                                    <Mail className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                                </a>
                                <button type="button" onClick={handleCopy} title={copied ? "Lien copié !" : copyError ? "Impossible de copier" : "Copier le lien"} className="w-10 h-10 bg-card border border-border rounded-lg flex items-center justify-center hover:bg-primary/10 hover:border-primary/30 transition-all group liquid-glass">
                                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground group-hover:text-primary" />}
                                </button>
                            </div>
                        </div>

                        {/* Article Content */}
                        <div className="lg:col-span-11">
                            {headlineUrl && (
                                <div className="mb-6 sm:mb-8 aspect-video bg-gradient-to-br from-primary/20 via-accent/10 to-primary/20 rounded-xl sm:rounded-2xl overflow-hidden border border-border flex items-center justify-center">
                                    {isVideo ? (
                                        <video src={headlineUrl} controls className="w-full h-full object-cover" playsInline />
                                    ) : (
                                        <img src={headlineUrl} alt={post.title} className="w-full h-full object-cover" />
                                    )}
                                </div>
                            )}

                            <article className="max-w-none article-body">
                                <div 
                                    className="prose prose-xs sm:prose-sm md:prose-base lg:prose-lg xl:prose-xl dark:prose-invert prose-primary max-w-none prose-img:rounded-xl prose-img:shadow-lg prose-headings:scroll-mt-20 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-ul:list-none prose-ol:list-none"
                                    dangerouslySetInnerHTML={{ __html: post.body }} 
                                />
                                <style>{`
                                    .article-body {
                                        overflow-x: hidden;
                                    }
                                    .article-body img {
                                        max-width: 100% !important;
                                        height: auto !important;
                                        display: block;
                                        margin-left: auto !important;
                                        margin-right: auto !important;
                                        border-radius: 0.75rem;
                                        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                                    }
                                    @media (max-width: 640px) {
                                        .article-body img {
                                            border-radius: 0.5rem;
                                            max-width: 100% !important;
                                            height: auto !important;
                                        }
                                    }
                                    .article-body iframe {
                                        max-width: 100% !important;
                                        height: auto !important;
                                        aspect-ratio: 16 / 9;
                                        border-radius: 0.75rem;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body iframe {
                                            aspect-ratio: 16 / 9;
                                            border-radius: 0.5rem;
                                        }
                                    }
                                    .article-body video {
                                        max-width: 100% !important;
                                        height: auto !important;
                                        display: block;
                                        margin-left: auto !important;
                                        margin-right: auto !important;
                                        border-radius: 0.75rem;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body video {
                                            border-radius: 0.5rem;
                                        }
                                    }
                                    .article-body figure {
                                        margin: 1.5rem 0 !important;
                                        max-width: 100% !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body figure {
                                            margin: 1rem 0 !important;
                                        }
                                    }
                                    .article-body figure img {
                                        margin-bottom: 0.5rem !important;
                                    }
                                    .article-body figcaption {
                                        font-size: 0.875rem !important;
                                        color: #6b7280 !important;
                                        text-align: center !important;
                                        margin-top: 0.5rem !important;
                                        padding: 0.5rem !important;
                                    }
                                    .article-body table {
                                        width: 100% !important;
                                        table-layout: auto !important;
                                        overflow-x: auto;
                                        display: block;
                                        border-radius: 0.5rem;
                                        margin: 1.5rem 0 !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body table {
                                            font-size: 0.875rem !important;
                                            margin: 1rem 0 !important;
                                        }
                                    }
                                    .article-body pre {
                                        max-width: 100% !important;
                                        overflow-x: auto !important;
                                        white-space: pre-wrap !important;
                                        word-wrap: break-word !important;
                                        border-radius: 0.5rem;
                                        margin: 1.5rem 0 !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body pre {
                                            font-size: 0.875rem !important;
                                            margin: 1rem 0 !important;
                                        }
                                    }
                                    .article-body code {
                                        word-wrap: break-word !important;
                                        overflow-wrap: break-word !important;
                                    }
                                    .article-body p, .article-body h1, .article-body h2, .article-body h3, .article-body h4, .article-body h5, .article-body h6, .article-body ul, .article-body ol, .article-body li {
                                        max-width: 100% !important;
                                        overflow-wrap: break-word !important;
                                        word-wrap: break-word !important;
                                        word-break: break-word !important;
                                    }
                                    .article-body h1 {
                                        font-size: clamp(1.5rem, 4vw, 2.25rem) !important;
                                        line-height: 1.2 !important;
                                    }
                                    .article-body h2 {
                                        font-size: clamp(1.25rem, 3vw, 1.875rem) !important;
                                        line-height: 1.3 !important;
                                    }
                                    .article-body h3 {
                                        font-size: clamp(1.125rem, 2.5vw, 1.5rem) !important;
                                        line-height: 1.4 !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body p {
                                            font-size: 0.95rem !important;
                                            line-height: 1.7 !important;
                                        }
                                    }
                                    .article-body blockquote {
                                        border-left-width: 4px !important;
                                        padding-left: 1rem !important;
                                        margin: 1.5rem 0 !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body blockquote {
                                            margin: 1rem 0 !important;
                                            padding-left: 0.75rem !important;
                                        }
                                    }
                                    .article-body ul, .article-body ol {
                                        margin-bottom: 1.5rem !important;
                                        padding-left: 2rem !important;
                                        margin-top: 1rem !important;
                                    }
                                    .article-body li {
                                        margin-bottom: 0.75rem !important;
                                        line-height: 1.75 !important;
                                        padding-left: 0.5rem !important;
                                    }
                                    .article-body ul {
                                        list-style-type: disc !important;
                                        list-style-position: outside !important;
                                    }
                                    .article-body ul li::marker {
                                        color: rgb(59 130 246) !important;
                                        font-size: 1.1em !important;
                                    }
                                    .article-body ol {
                                        list-style-type: decimal !important;
                                        list-style-position: outside !important;
                                    }
                                    .article-body ol li::marker {
                                        color: rgb(59 130 246) !important;
                                        font-weight: 700 !important;
                                        font-size: 1em !important;
                                    }
                                    @media (max-width: 640px) {
                                        .article-body ul, .article-body ol {
                                            padding-left: 1.5rem !important;
                                        }
                                        .article-body li {
                                            margin-bottom: 0.5rem !important;
                                        }
                                    }
                                `}</style>
                            </article>

                            {/* Share Section (Mobile) */}
                            <div className="lg:hidden mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
                                <h3 className="text-xs sm:text-sm font-bold text-foreground mb-3 sm:mb-4">Partager cet article</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                    <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors liquid-glass">
                                        <Facebook className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Facebook</span>
                                    </a>
                                    <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-sky-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-sky-600 transition-colors liquid-glass">
                                        <Twitter className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Twitter</span>
                                    </a>
                                    <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-blue-700 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-800 transition-colors liquid-glass">
                                        <Linkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">LinkedIn</span>
                                    </a>
                                    <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-green-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-green-600 transition-colors liquid-glass">
                                        <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">WhatsApp</span>
                                    </a>
                                    <button type="button" onClick={handleCopy} className="col-span-2 sm:col-span-1 flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-secondary text-foreground rounded-lg text-xs sm:text-sm font-medium hover:bg-secondary/80 transition-colors">
                                        {copied ? <><Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Lien copié !</> : copyError ? <><Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Impossible</> : <><Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Copier</>}
                                    </button>
                                </div>
                            </div>

                            {/* Author Card */}
                            <div className="mt-6 sm:mt-8 bg-card p-4 sm:p-6 rounded-xl border border-border">
                                <div className="flex items-start gap-3 sm:gap-4">
                                    <Link to={`/auteur/${authorId}`}>
                                        {authorAvatar ? (
                                            <img src={authorAvatar} alt={authorName} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0 hover:ring-2 hover:ring-primary transition-all" />
                                        ) : (
                                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                                                {authorName.charAt(0)}
                                            </div>
                                        )}
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/auteur/${authorId}`} className="text-sm sm:text-base font-bold text-foreground mb-1 hover:text-primary transition-colors block truncate">
                                            {authorName}
                                        </Link>
                                        <p className="text-xs text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                                            {post.author?.biography || "Contributeur a l'Universite de Ngozi"}
                                        </p>
                                        <Link to={`/auteur/${authorId}`} className="px-3 py-1 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-all inline-block border-2 border-primary-foreground/20">
                                            Voir le profil
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <section className="py-8 sm:py-12 bg-secondary/30">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6">
                        <div className="flex items-center gap-2 mb-4 sm:mb-6">
                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                            <h2 className="text-xl sm:text-2xl font-bold text-foreground">Articles Similaires</h2>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {relatedPosts.map((rp) => {
                                const rpImg = rp.headlineImage?.url || rp.headlineImage || ''
                                return (
                                    <article key={rp._id} onClick={() => navigate(`/article/${rp._id}`)} className="group cursor-pointer bg-card rounded-xl border border-border hover:shadow-lg transition-all overflow-hidden">
                                        {rpImg && (
                                            <div className="aspect-video relative bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
                                                <img src={rpImg} alt={rp.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            </div>
                                        )}
                                        <div className="p-4 sm:p-5">
                                            <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-semibold mb-2">
                                                {getCategoryLabel(rp.category)}
                                            </span>
                                            <h3 className="text-sm sm:text-base font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">{rp.title}</h3>
                                            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{rp.excerpt}</p>
                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                <span>{new Date(rp.publishedAt || rp.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                                                <span className="flex items-center gap-1"><ChevronRight className="w-3 h-3" /> Lire</span>
                                            </div>
                                        </div>
                                    </article>
                                )
                            })}
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
}
